'use client';

import { c as _c } from "react/compiler-runtime";
import * as Dialog from '@radix-ui/react-dialog';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect, useState } from 'react';
import { cn } from '@/utils/tailwind';
export type MobileDrawerProps = {
  side: 'left' | 'right';
  trigger: ReactNode;
  /** Optional title for a11y; rendered visually-hidden if not provided as visible. */
  title?: string;
  children: ReactNode;
};

/**
 * Full-height slide-in drawer for mobile breakpoints, implemented with Radix Dialog.
 * Closes automatically on route change.
 *
 * @param props.side - Which edge the drawer slides in from ('left' or 'right').
 * @param props.trigger - Element rendered as the Dialog trigger (e.g. a hamburger button).
 * @param props.title - Accessible title for the dialog; rendered visually-hidden. Defaults to 'Menu'.
 * @param props.children - Drawer body content.
 */
export function MobileDrawer(t0) {
  const $ = _c(23);
  const {
    side,
    trigger,
    title: t1,
    children
  } = t0;
  const title = t1 === undefined ? "Menu" : t1;
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setOpen(false);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== pathname) {
    t3 = [pathname];
    $[1] = pathname;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== trigger) {
    t4 = <Dialog.Trigger asChild={true}>{trigger}</Dialog.Trigger>;
    $[3] = trigger;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Dialog.Overlay className="data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 fixed inset-0 z-40 bg-black/60 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = side === "left" && "left-0 border-r-2";
  const t7 = side === "right" && "right-0 border-l-2";
  const t8 = side === "left" && "data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left";
  const t9 = side === "right" && "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right";
  let t10;
  if ($[6] !== t6 || $[7] !== t7 || $[8] !== t8 || $[9] !== t9) {
    t10 = cn("fixed inset-y-0 z-50 flex w-80 max-w-[85vw] flex-col border-0 border-border bg-background shadow-lg", t6, t7, "data-[state=closed]:animate-out data-[state=open]:animate-in", t8, t9);
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== title) {
    t11 = <Dialog.Title className="sr-only">{title}</Dialog.Title>;
    $[11] = title;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== children) {
    t12 = <div className="flex-1 overflow-y-auto p-3">{children}</div>;
    $[13] = children;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t12) {
    t13 = <Dialog.Portal>{t5}<Dialog.Content className={t10}>{t11}{t12}</Dialog.Content></Dialog.Portal>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== open || $[20] !== t13 || $[21] !== t4) {
    t14 = <Dialog.Root open={open} onOpenChange={setOpen}>{t4}{t13}</Dialog.Root>;
    $[19] = open;
    $[20] = t13;
    $[21] = t4;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}